import React, { useState, useCallback, useEffect } from "react";
import { usePersistSetting } from "hooks";
import { produce } from "immer";
import Formsy from "formsy-react";
import Icons from "@hs/icons";
import Button from "react-bootstrap/lib/Button";
import Status from "components/Status";
import { InputWithFormsy as Input } from "./Input";
import Recents from "./Recents";
import Advanced from "./Advanced";
import Filters from "./Filters";
import SearchScope from "./SearchScope";
import Constants from "data/constants";
import { getLocalizedText, getClassNames } from "data/utils";
import { SearchValueType, type SearchValueTypeEnum } from "data/types";
import styles from "./Mask.module.css";

export const getValueType = (name: string): SearchValueTypeEnum => {
    switch (name) {
        case "ItemType":
            return SearchValueType.ItemType;
        case "Geaendert_am":
        case "Erstellt_am":
            return SearchValueType.RelativeDate;
        case "Groesse":
            return SearchValueType.FileSize;
        default:
            return SearchValueType.String;
    }
};

export const fixFilterValue = (value: any) => {
    switch (true) {
        case value?.$type === "user":
            return value.displayName;
        case !!value?.T:
            return value.T;
        default:
            return value;
    }
};

export const handleItemTypeChange = (filters) => {
    filters = filters
        .filter((f) => ["Groesse", "Dateityp"].includes(f.name))
        .forEach((f) => {
            f.value = null;
        });
};

type Props = {
    /** initial search text */
    sayt: string,
    /** callback to set search text */
    setSayt: (sayt: string) => void,
    /** initial search scope itemUri */
    itemUri: string,
    /** initial search scope */
    searchScope: SearchScopeEnum,
    /** callback when user searches
     * @param {SAYTOptionDTO} item search item
     * @param {SearchScopeEnum} searchScope search scope
     * @param {string} itemUri scoped itemUri
     * @param {Array<SearchFilterDTO>} preparedFilters prepared filters
     * */
    onSearch: (
        item: SAYTOptionDTO,
        searchScope: SearchScopeEnum,
        itemUri: string,
        preparedFilters: Array<SearchFilterDTO>
    ) => void,
    /** callback when user changes search scope
     * @param {SearchScopeEnum} searchScope new search scope
     * @param {string} itemUri new scoped itemUri
     */
    onScopeChange: (searchScope: SearchScopeEnum, itemUri: string) => void,
    /** default advanced divider state */
    showSearchText: boolean,
    /** default search scope divider state */
    showSearchScope: boolean,
};

const Mask = ({
    sayt,
    setSayt,
    itemUri: itemUriFromProps,
    searchScope: searchScopeFromProps,
    onSearch,
    onScopeChange,
    showSearchText: advanced = false,
    showSearchScope = false,
    filters: filtersFromProps = [],
}: Props) => {
    const [showSearchText] = usePersistSetting({
        name: "showSearchText",
        defaultValue: advanced,
    });

    useEffect(() => {
        setTimeout(() => {
            document
                ?.getElementById(showSearchText ? "words" : "simpleText")
                ?.focus();
        }, 0);
    }, [showSearchText]);

    // Simple Search
    const [simpleText, setSimpleText] = useState(sayt);
    const handleTextChange = useCallback(
        (value) => {
            setSimpleText(value);
            setSayt(value);
        },
        [setSayt]
    );

    // Search Scope
    const [searchScope, setSearchScope] = useState(searchScopeFromProps);
    const [itemUri, setItemUri] = useState(itemUriFromProps);
    const handleScopeChange = useCallback(
        (searchScope, itemUri) => {
            setSearchScope(searchScope);
            setItemUri(itemUri);
            /* istanbul ignore else */
            if (typeof onScopeChange === "function")
                onScopeChange(searchScope, itemUri);
        },
        [onScopeChange]
    );

    // Advanced Search & Filters
    const [searchText, setSearchText] = useState({
        words: null,
        phrase: null,
        anyWords: null,
        nonWords: null,
    });
    const [filters, setFilters] = useState(filtersFromProps);
    const handleSearch = useCallback(() => {
        setIsSubmitting(true);
        const item = {
            value: showSearchText ? searchText : simpleText,
        };
        const preparedFilters = produce(filters, (draft) =>
            draft
                .map((f) => ({
                    ...f,
                    value: fixFilterValue(f.value),
                }))
                .filter((f) => !!f.value)
        );
        onSearch(item, searchScope, itemUri, preparedFilters);
        setSayt("");
    }, [
        setSayt,
        onSearch,
        showSearchText,
        searchText,
        simpleText,
        searchScope,
        itemUri,
        filters,
    ]);
    const handleSearchTextChange = useCallback(
        (field: string, value: any) =>
            setSearchText((searchText) =>
                produce(searchText, (draft) => {
                    draft[field.split(".")[1]] = value;
                })
            ),
        []
    );
    const handleFilterChange = useCallback(
        (field: string, value: any) =>
            setFilters((filters) =>
                produce(filters, (draft) => {
                    const name = field.split(".")[1];
                    const index = draft.findIndex(
                        (filter) => filter.name === name
                    );
                    if (index === -1) {
                        draft.push({
                            name,
                            valueType: getValueType(name),
                            value,
                        });
                    } else {
                        draft[index].value = value;
                    }
                    if (name === "ItemType") return handleItemTypeChange(draft);
                })
            ),
        []
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);
    const enableSubmit = useCallback(() => setCanSubmit(true), []);
    const disableSubmit = useCallback(() => setCanSubmit(false), []);

    return (
        <Formsy
            className={getClassNames(styles.form, "search-op-hidden")}
            onValidSubmit={handleSearch}
            onValid={enableSubmit}
            onInvalid={disableSubmit}
            data-test="SearchMask"
        >
            <Input
                value={!showSearchText ? simpleText : ""}
                id="simpleText"
                name="sm.input"
                onChange={handleTextChange}
                className={styles.input}
                disabled={isSubmitting || showSearchText}
                data-test="SearchMask.Input"
            >
                <Icons.Library
                    name="magnifying-glass"
                    onClick={
                        !(isSubmitting || showSearchText)
                            ? handleSearch
                            : undefined
                    }
                />
            </Input>
            <Recents
                sayt={sayt}
                itemUri={itemUri}
                onSearch={onSearch}
                defaultExpanded={!advanced}
            />
            <Advanced
                searchText={searchText}
                onSearchTextChange={handleSearchTextChange}
                defaultExpanded={advanced}
            />
            <Filters
                itemUri={itemUri}
                filters={filters}
                onFilterChange={handleFilterChange}
                defaultExpanded={advanced}
            />
            <SearchScope
                itemUri={itemUri}
                searchScope={searchScope}
                onScopeChange={handleScopeChange}
                defaultExpanded={showSearchScope}
            />
            <Button
                disabled={!canSubmit || /*istanbul ignore next*/ isSubmitting}
                type="submit"
                aria-label={getLocalizedText("$search:mask.submit")}
                className={styles.submit}
                bsStyle="primary"
                data-test="SearchMask.Submit"
            >
                {isSubmitting && (
                    <Status status={Constants.LOADING} inline={true} />
                )}{" "}
                {getLocalizedText("$search:mask.submit")}
            </Button>
        </Formsy>
    );
};
Mask.displayName = "SearchMask.Mask";
export default Mask;
